import React, { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * PIN gate para el módulo Laboral. Reutiliza el mismo endpoint personal
 * `/api/users/me/pin/verify` que Cashflow Pro. Si el usuario no tiene PIN
 * configurado, lo lleva a crear uno.
 *
 * Se recuerda por sessionStorage para no pedirlo de nuevo si navega entre
 * los 4 sub-módulos de LABORAL en la misma sesión.
 */
export default function LaboralPinGate({ children }) {
  const KEY = "laboral_pin_unlocked_v1";
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(KEY) === "1");
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [setupPhase, setSetupPhase] = useState(null); // null | 'create'
  const [newPin, setNewPin] = useState("");
  const [newPin2, setNewPin2] = useState("");

  if (unlocked) return children;

  const submit = async (e) => {
    e?.preventDefault();
    if (!pin || pin.length < 4) return;
    setBusy(true);
    try {
      const { data } = await api.post("/users/me/pin/verify", { pin });
      if (data?.ok) {
        sessionStorage.setItem(KEY, "1");
        setUnlocked(true);
        setError(false);
      } else if (data?.needs_setup) {
        setSetupPhase("create");
      } else {
        setError(true);
        setPin("");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail.toLowerCase().includes("no pin") || err?.response?.status === 404) {
        setSetupPhase("create");
      } else {
        setError(true);
        setPin("");
      }
    } finally {
      setBusy(false);
    }
  };

  const create = async (e) => {
    e?.preventDefault();
    if (newPin.length < 4 || newPin.length > 6 || !/^\d+$/.test(newPin)) {
      toast.error("El PIN debe ser 4-6 dígitos");
      return;
    }
    if (newPin !== newPin2) {
      toast.error("Los PIN no coinciden");
      return;
    }
    setBusy(true);
    try {
      await api.post("/users/me/pin", { new_pin: newPin });
      sessionStorage.setItem(KEY, "1");
      toast.success("PIN guardado");
      setUnlocked(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando PIN");
    } finally {
      setBusy(false);
    }
  };

  // Setup screen — crear PIN si no lo tiene
  if (setupPhase === "create") {
    return (
      <div className="mx-auto max-w-md py-16" data-testid="laboral-pin-setup">
        <div className="rounded-md border bg-white p-8 shadow-sm">
          <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" /> Configurar PIN Laboral
          </div>
          <h1 className="mb-1 font-heading text-2xl font-bold">Elige tu PIN</h1>
          <p className="mb-5 text-xs text-muted-foreground">
            4 a 6 dígitos. Te lo pediremos cada vez que entres a la zona Laboral.
          </p>
          <form onSubmit={create} className="space-y-3">
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-wider text-muted-foreground">Nuevo PIN</label>
              <Input
                autoFocus type="password" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="tabular text-center text-2xl tracking-[0.5em]"
                data-testid="laboral-new-pin"
              />
            </div>
            <div>
              <label className="mb-1 block text-[10px] uppercase tracking-wider text-muted-foreground">Repite el PIN</label>
              <Input
                type="password" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                value={newPin2}
                onChange={(e) => setNewPin2(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="tabular text-center text-2xl tracking-[0.5em]"
                data-testid="laboral-new-pin-2"
              />
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="laboral-create-pin-submit">
              {busy ? "Guardando…" : "Guardar y entrar"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Verify screen — pedir PIN existente
  return (
    <div className="mx-auto max-w-md py-16" data-testid="laboral-pin-gate">
      <div className="rounded-md border bg-white p-8 shadow-sm">
        <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5 text-primary" /> Zona Laboral protegida
        </div>
        <h1 className="mb-1 font-heading text-2xl font-bold">Introduce tu PIN</h1>
        <p className="mb-5 text-xs text-muted-foreground">
          Fichas, nóminas y salarios del equipo requieren el PIN personal.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <Input
            autoFocus type="password" inputMode="numeric" pattern="[0-9]*" maxLength={6}
            value={pin}
            onChange={(e) => { setPin(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(false); }}
            className={`tabular text-center text-3xl tracking-[0.7em] ${error ? "border-red-400 ring-2 ring-red-200" : ""}`}
            data-testid="laboral-pin-input"
          />
          {error && (
            <div className="text-center text-xs text-red-600" data-testid="laboral-pin-error">PIN incorrecto</div>
          )}
          <Button type="submit" className="w-full" disabled={busy || pin.length < 4} data-testid="laboral-pin-submit">
            {busy ? "Verificando…" : "Desbloquear"}
          </Button>
        </form>
      </div>
    </div>
  );
}
